import React, { FC, useState, useRef } from 'react'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router-dom'
import { IonPage, IonContent, useIonViewWillEnter } from '@ionic/react'
import { memberMobx, giftCardMobx } from '@mobxStore'
import { useHistoryNew, checkExpire, modalLayer } from '@utils'
import { deepClone, ddayjs } from '@dt/fe-utils'
import { useGiftCardDecorationInit, useGiftCardPageTitle } from '@common/hooks'
import BindGiftCardModal from '../components/BindGiftCardModal'
import GiftCardDetail from '../components/GiftCardDetail'
import CardSort from '../components/CardSort'
import mktController from 'controllers/mktController'
import { MktHeader, CardShape } from 'pages/mkt/components'
import { cloneDeep, filter } from '@dt/fe-utils/dist/lodash'
import i18n from 'i18next'
import { getGiftCardName } from '@common/utils/giftcard'
import noGiftCardImg from '@img/no-giftcard.png'
import './index.scss'

interface IProps extends IPage, RouteComponentProps { }
type TListSortType = 'timeAsc' | 'timeDes' | 'balanceAsc' | 'balanceDes'

const SORT_NUM_MIN = 6 // 当礼品卡数量大于 SORT_NUM_MIN 个时展示排序
const DEFAULT_SORT = 'timeDes'

const sortFunc = (cardList: IMemberGiftCardInfo[], sortType: string) =>
  cardList.sort((cardA: IMemberGiftCardInfo, cardB: IMemberGiftCardInfo) => {
    if (sortType.includes('balance')) {
      return sortType.includes('Asc') ? cardB.balance - cardA.balance : cardA.balance - cardB.balance
    }
    return sortType.includes('Asc') ? new Date(cardB.addTime).getTime() - new Date(cardA.addTime).getTime() : new Date(cardA.addTime).getTime() - new Date(cardB.addTime).getTime()
  })

const GiftCard: FC<IProps> = ({ match }) => {
  const history = useHistoryNew()
  const { getMemberGiftCards, memberInfo } = memberMobx
  const { eGiftCardList, getEGiftCardList, getGiveCardFlag, hasGiveCard } = giftCardMobx
  const [rawIndex, setRawIdex] = useState<number>() // 展开的礼品卡在数组中的原始位置
  const [myCards, setMyCards] = useState<IMemberGiftCardInfo[]>([])
  const [anInfo, setAnInfo] = useState<{
    type: 'top-show' | 'move' | '';
    anIndex: number;
    moveY?: number;
  }>({
    type: '',
    anIndex: -1,
  })
  const [listSort, setListSort] = useState<TListSortType>(DEFAULT_SORT) // 礼品卡列表排序
  const showCardRef = useRef<number>(-1)
  const contentRef = useRef<any>(null)
  const scrollTop = useRef<number>(0)
  const handleGo = (path: string) => {
    history.push(path)
  }
  const handleBind = () => {
    mktController.checkLogin(history).then(() => {
      modalLayer(BindGiftCardModal, null, 'bindGiftCardModal').then(() => {
        getGiftCardList()
      })
    })
  }
  const getCardList = () => {
    return myCards.map((item: IMemberGiftCardInfo, index: number) => {
      let anClass: string = ''
      if (anInfo.type === 'top-show' && anInfo.anIndex === index) {
        anClass = anInfo.type
      }
      if (anInfo.type === 'move' && anInfo.anIndex === index) {
        anClass = anInfo.type
      }
      if (showCardRef.current === item.giftcardId && anClass !== 'top-show') {
        anClass = 'top-show'
      }
      const shape = item?.extra?.shape || 'rectangle'
      return (
        <div
          id={`card-item-${myCards.length - 1 - index}`}
          className={`card-blcok ${anClass === 'top-show' && myCards.length === 1 ? '' : anClass}`}
          key={item.memberCardId}
          style={{
            transform: anClass === 'move' ? `translateY(-${anInfo.moveY}px)` : anInfo.type === 'move' && anClass !== 'move' ? 'translateY(88vh)' : 'none'
          }}
          data-cypid='mmgSortGCard'
          onClick={(e: any) => { cardsSort(index, e) }}
        >
          <div className='card-face-block' style={{ height: `calc(${94 * 0.67}vw + 30px)` }}>
            <CardShape
              cardName={item.giftcard.giftcardName}
              balance={item.balance}
              scene='myCards'
              cardFace={mktController.getGiftCardCover(item)}
              border='shadow'
              shape={shape}
              isScale={false}
            />
          </div>
          {anClass === 'top-show' && <div className='card-detail-block' style={{ height: `${shape === 'rectangle' ? 'calc(88vh - 30vw)' : 'calc(88vh - 62vw)'}` }}><IonContent className='card-detail-content'><GiftCardDetail cardInfo={item}></GiftCardDetail></IonContent></div>}
        </div>
      )
    })
  }

  const cardsSort = (sort: number, e: any) => {
    if (sort === 0) {
      contentRef.current.scrollToTop()
      if (anInfo.type === 'top-show') {
        hideCardDetail()
      } else {
        setAnInfo({
          type: 'top-show',
          anIndex: sort
        })
      }
    } else {
      contentRef.current.scrollToTop()
      const pos: number = (e.clientY - e.nativeEvent.offsetY - 58) + scrollTop.current
      setAnInfo({
        type: 'move',
        anIndex: sort,
        moveY: pos
      })
      setRawIdex(sort)
      setTimeout(() => {
        const newCards = cloneDeep(myCards)
        const curCard = newCards.splice(sort, 1)
        const resCard = curCard.concat(newCards)
        saveShowCardRef(resCard[0].giftcardId)
        setMyCards(resCard)
        setAnInfo({
          type: 'top-show',
          anIndex: 0
        })
        scrollTop.current = 0
      }, 400)
    }
  }
  // 修改礼品卡列表排列顺序
  const handleChangeSort = (sort: TListSortType, cardList: IMemberGiftCardInfo[], isInit?: boolean) => {
    // 收起展开卡片
    setAnInfo({ type: '', anIndex: -1 })
    saveShowCardRef(-1)

    localStorage.setItem('giftCardListSort', sort)
    let newListCard = deepClone(cardList)
    // 假如目标是按时间降序，先按金额降序，再按时间降序
    newListCard = sortFunc(newListCard, sort.includes('time') ? sort.replace('time', 'balance') : sort.replace('balance', 'time'))
    newListCard = sortFunc(newListCard, sort)
    if (sort === 'timeAsc' || sort === 'timeDes') { // 按时间排序时兼容老商家排序逻辑，始终把0元卡放底部
      const zeroCards = []
      newListCard = newListCard.filter(card => {
        if (card.balance === 0) zeroCards.push(card)
        return card.balance !== 0
      })
      newListCard = [...zeroCards, ...newListCard]
    }
    setMyCards(newListCard)
    setListSort(sort)
    setTimeout(() => {
      document.getElementById('card-item-0').scrollIntoView({ behavior: 'smooth' })
    }, isInit ? 500 : 50)
  }
  const hideCardDetail = () => {
    saveShowCardRef(-1)
    setAnInfo({
      type: '',
      anIndex: -1
    })
    setRawIdex(undefined)
    const newCards = cloneDeep(myCards)
    const [curCard] = newCards.splice(0, 1)
    newCards.splice(rawIndex, 0, curCard)
    setMyCards(newCards)
  }
  const saveShowCardRef = (cardId?: number) => {
    showCardRef.current = cardId
  }
  const handleScroll = (e: any) => {
    scrollTop.current = e.detail.scrollTop
    if (anInfo.type === '' || anInfo.type === 'move') return
    if (e.detail.scrollTop >= 50) {
      hideCardDetail()
    }
  }
  const goBuyCard = () => {
    const routeInfo: any = history.getRouteInfo()
    if (routeInfo?.lastPathname?.indexOf('/giftCard') > -1) {
      history.goBack()
    } else {
      history.push('/giftCard/2')
    }
  }
  const getGiftCardList = (isInit?: boolean) => {
    getMemberGiftCards().then((list) => {
      const res = filter(list, (i: any) => {
        const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs('brand').format('YYYY-MM-DD HH:mm:ss')) : false
        return i.status === 'enable' && !isExpired && (i.balance > 0 || i.giftcard.allowAgainLoad)
      })
      if (res?.length) {
        const sort = res.length > SORT_NUM_MIN ? localStorage.getItem('giftCardListSort') : DEFAULT_SORT
        // 当礼品卡数量小于 SORT_NUM_MIN 时，默认按时间升排序
        handleChangeSort((sort as TListSortType) || DEFAULT_SORT, res, isInit)
      }
    })
  }
  useIonViewWillEnter(() => {
    getGiftCardList(true)
    getGiveCardFlag(memberInfo?.memberId)
    if (!eGiftCardList.length) {
      getEGiftCardList()
    }
  })
  useGiftCardDecorationInit()
  useGiftCardPageTitle()
  const showSort = myCards?.length > SORT_NUM_MIN // 当礼品卡数量大于 SORT_NUM_MIN 个时展示排序
  return (
    <IonPage className='my-giftcard-page'>
      <MktHeader onBack={() => mktController.goBack(history)} title={getGiftCardName('mkt.giftCard.myGiftCard', 'mkt.giftCard.myGiftCardCustom')}>
        <div className='giftcard-opt'>
          <div data-cypid='mmgToBindGCard' className='opt-btn' onClick={() => handleBind()}>
            <i className='iconfont icon-add2'></i>
            <span>{i18n.t('mkt.common.bind')}</span>
          </div>
          <div data-cypid='mmgToGiveGCard' className={`opt-btn${hasGiveCard ? ' opt-btn-give' : ''}`} onClick={() => handleGo('iGc/giftRecord')}>
            <i className='iconfont icon-orders'></i>
            <span>{i18n.t('mkt.giftCard.transferRecord')}</span>
          </div>
          <div data-cypid='mmgToDisabledGCard' className='opt-btn' onClick={() => handleGo('iGc/giftAvailable')}>
            <i className='iconfont icon-forbid'></i>
            <span>{i18n.t('mkt.giftCard.unavailableCard')}</span>
          </div>
        </div>
        {showSort && (
          <CardSort
            sort={listSort}
            onSortChange={(sort) => handleChangeSort(sort, myCards)}
          />
        )}
      </MktHeader>
      <IonContent ref={contentRef} className='page-content' scrollEvents onIonScroll={handleScroll}>
        {
          myCards.length
            ? (
              <div className={`card-list${showSort ? ' card-list-sort' : ''}`}>
                {getCardList()}
              </div>
            )
            : (
              <div className='null-block'>
                <div className='null-block-img' style={{ backgroundImage: `url(${noGiftCardImg})` }}></div>
                <div className='null-block-text'>{i18n.t('mkt.giftCard.noGiftCard')}</div>
                <div className='null-block-desc'>{i18n.t('mkt.giftCard.myGcNullTip')}</div>
                {eGiftCardList.length > 0 && <div data-cypid='mmgToBuyGCard' className='null-block-btn' onClick={goBuyCard}>{getGiftCardName('pageName.buyGiftCard', 'pageName.buyGiftCardCustom')}</div>}
              </div>
            )
        }
        {myCards.length > 2 && anInfo.type === 'top-show' && <div data-cypid='mmgCollapseGCard' className='pull-up' onClick={hideCardDetail}><div className='pull-up-tip'>{i18n.t('mkt.member.pullUp')}<i className='iconfont icon-daohangzhankai'></i></div></div>}
      </IonContent>
    </IonPage>
  )
}

export default observer(GiftCard)
